import { useId } from 'react';
import { motion } from 'motion/react';
import { Emoji } from '@/ui/Emoji';
import { cn } from '@/ui/util';
import { useSound } from './useSound';

export interface SoundToggleProps {
  /** Show a volume slider next to the button. */
  showVolume?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/** 🔊 / 🔇 toggle (+ optional volume slider) bound to the global sound engine. */
export function SoundToggle({ showVolume, size = 'md', className }: SoundToggleProps) {
  const { muted, volume, toggleMuted, setVolume, play } = useSound();
  const id = useId();
  const off = muted || volume <= 0;
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <motion.button
        type="button"
        // Action-style label (what pressing does); no aria-pressed so the two don't contradict.
        aria-label={off ? 'Sesi aç' : 'Sesi kapat'}
        title={off ? 'Sesi aç' : 'Sesi kapat'}
        whileTap={{ scale: 0.85, rotate: -10 }}
        onClick={() => {
          const turningOn = off;
          if (turningOn && volume <= 0) setVolume(0.6);
          toggleMuted();
          if (turningOn) setTimeout(() => play('click'), 0);
        }}
        className={cn(
          'flex items-center justify-center border-ink bg-white shadow-ink-xs transition-colors hover:bg-yellow-soft active:translate-y-0.5 active:shadow-none',
          size === 'sm' ? 'size-8 border-2' : 'size-10 border-3',
          off && 'bg-[#eee8d8]',
        )}
      >
        <Emoji emoji={off ? '🔇' : '🔊'} label="" size={size === 'sm' ? 18 : 22} />
      </motion.button>
      {showVolume && (
        <>
          <label htmlFor={id} className="sr-only">
            Ses düzeyi
          </label>
          <input
            id={id}
            type="range"
            min={0}
            max={100}
            step={5}
            value={Math.round(volume * 100)}
            onChange={(e) => setVolume(Number(e.target.value) / 100)}
            onPointerUp={() => play('bid')}
            disabled={muted}
            aria-valuetext={`%${Math.round(volume * 100)}`}
            className="comic-range w-24 disabled:opacity-40"
          />
        </>
      )}
    </span>
  );
}
